import { useQuery } from '@tanstack/react-query';
import {
  type BankMovementResponse,
  formatMoney,
  LOAN_REVERSAL_DAYS,
  type LoanReversalOption,
} from '@k-management/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { movementPath } from './treasury-ui.js';

/**
 * The loan instalment a reversal of the bank takes back (3.16): those of the
 * same amount in the days before it, the closest already chosen.
 */
export function ReversalPicker({
  code,
  movement,
  onChange,
}: {
  code: string;
  movement: BankMovementResponse;
  /** Null while no instalment is chosen. */
  onChange: (movementId: string | null) => void;
}) {
  const options = useQuery({
    queryKey: ['treasury', 'banks', code, 'reversal-options', movement.id],
    queryFn: () =>
      apiRequest<LoanReversalOption[]>(
        `/treasury/banks/${code}/movements/${movement.id}/reversal-options`,
      ),
  });
  const [pick, setPick] = useState<string | null>(null);
  useEffect(() => {
    if (options.data) setPick(options.data[0]?.movementId ?? null);
  }, [options.data]);
  useEffect(() => onChange(pick), [pick, onChange]);

  if (options.isLoading) return <p className="muted">Caricamento…</p>;
  if (options.error) return <p className="error">{options.error.message}</p>;
  if (!options.data?.length) {
    return (
      <p className="notice small">
        Nessuna rata di {formatMoney(movement.amount)} nei {LOAN_REVERSAL_DAYS} giorni prima dello
        storno: non è lo storno di una rata, oppure è già abbinata.
      </p>
    );
  }
  return (
    <div className="stack">
      <small className="muted">Rata stornata:</small>
      {options.data.map((item) => (
        <label key={item.movementId} className="checkbox">
          <input
            type="radio"
            name="reversal"
            checked={pick === item.movementId}
            onChange={() => setPick(item.movementId)}
          />{' '}
          <strong>{formatDate(item.date)}</strong> · {formatMoney(item.amount)} · {item.loanName} ·
          capitale {formatMoney(item.capital)}, interessi {formatMoney(item.interest)}, spese{' '}
          {formatMoney(item.fees)}{' '}
          <span className="pill info">
            {item.days === 0
              ? 'stesso giorno'
              : `${item.days} ${item.days === 1 ? 'giorno' : 'giorni'}`}
          </span>{' '}
          <Link to={movementPath(code, item.movementId)} target="_blank">
            apri
          </Link>
        </label>
      ))}
    </div>
  );
}
